import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import WPAgGrid from "../../../packages/WPAgGrid/WPAgGrid";

import AssignLicense from "../AssignLicense";
import { getLicenseManagementTableConfig } from "./LicenseManagementTable.config";
import RenewLicense from "./RenewLicense";
import TerminateLicense from "./TerminateLicense";
import LicenseViewUserDetails from "./viewUser";

import { licenseActionMenuKeys } from "../../../constants/LicenseManagement.constants";

import "./LicenseManagementTable.scss";

const LicenseManagementTable = ({
  rowData,
  licenseData,
  updateLicensePayload,
  showSubheaderFilters,
  onUserLicenseUpdate,
  licensePayload,
}) => {
  const { t } = useTranslation();

  const [currentLicenseFilters, setCurrentLicenseFilters] = useState([]);
  const [currentRoleFilters, setCurrentRoleFilters] = useState([]);
  const [currentSortDirection, setCurrentSortDirection] = useState("");
  const [currentSortBy, setCurrentSortBy] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [dateRange, setDateRange] = useState({ fromDate: null, toDate: null });
  const [activeModalType, setActiveModalType] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridOptions = useMemo(
    () => ({
      headerHeight: 32,
      groupHeaderHeight: 32,
    }),
    [],
  );

  const handleRoleFilterChange = useCallback(
    (filterData) => {
      const rolenames = filterData.roleNames || [];
      setCurrentRoleFilters(rolenames);
      updateLicensePayload?.({ rolenames, pageNumber: 1 });
    },
    [updateLicensePayload],
  );

  const handleStatusFilterChange = useCallback(
    (filterData) => {
      setCurrentLicenseFilters(filterData);
      updateLicensePayload?.({ licenses: filterData, pageNumber: 1 });
    },
    [updateLicensePayload],
  );

  const handleSortChange = useCallback(
    (sortData) => {
      const { sortBy, sortDirection } = sortData;
      setCurrentSortDirection(sortDirection);
      setCurrentSortBy(sortBy);
      updateLicensePayload?.({ sortBy, sortDirection });
    },
    [updateLicensePayload],
  );

  const handleCompanySearchChange = useCallback(
    (value) => {
      setCompanySearch(value);
      updateLicensePayload?.({ companyName: value, pageNumber: 1 });
    },
    [updateLicensePayload],
  );

  const handleActionMenuOption = useCallback((optionId, rowData) => {
    setActiveModalType(optionId);
    setSelectedUserData(rowData);
  }, []);

  const handleDateRangeChange = (range) => {
    const { fromDate = null, toDate = null } = range || {};
    setDateRange({ fromDate, toDate });
    updateLicensePayload({ fromDate, toDate, pageNumber: 1 });
  };

  /**
   * Syncs local state with external licensePayload changes.
   * Batches all state updates for optimal performance.
   */
  useEffect(() => {
    if (!licensePayload) return;

    const {
      licenses = [],
      rolenames = [],
      companyName = "",
      fromDate = null,
      toDate = null,
    } = licensePayload;

    const licensesStr = JSON.stringify(licenses);
    const rolenamesStr = JSON.stringify(rolenames);
    const currentLicensesStr = JSON.stringify(currentLicenseFilters);
    const currentRolenamesStr = JSON.stringify(currentRoleFilters);

    licensesStr !== currentLicensesStr && setCurrentLicenseFilters(licenses);
    rolenamesStr !== currentRolenamesStr && setCurrentRoleFilters(rolenames);
    companyName !== companySearch && setCompanySearch(companyName);
    (fromDate !== dateRange.fromDate || toDate !== dateRange.toDate) &&
      setDateRange({ fromDate, toDate });
  }, [
    licensePayload,
    currentLicenseFilters,
    currentRoleFilters,
    companySearch,
    dateRange,
  ]);

  const LICENSE_MANAGEMENT_TABLE_CONFIG = useMemo(
    () =>
      getLicenseManagementTableConfig(
        licenseData,
        handleRoleFilterChange,
        handleStatusFilterChange,
        currentLicenseFilters,
        currentRoleFilters,
        showSubheaderFilters,
        handleSortChange,
        currentSortDirection,
        currentSortBy,
        handleCompanySearchChange,
        companySearch,
        handleActionMenuOption,
        handleDateRangeChange,
        dateRange.fromDate,
        dateRange.toDate,
      )?.map((col) => ({
        ...col,
        headerName: t(col.headerName),
        ...(Array.isArray(col.children) ? { children: col.children } : {}),
      })),
    [
      t,
      licenseData,
      handleRoleFilterChange,
      handleStatusFilterChange,
      currentLicenseFilters,
      currentRoleFilters,
      showSubheaderFilters,
      handleSortChange,
      currentSortDirection,
      currentSortBy,
      handleCompanySearchChange,
      companySearch,
      handleActionMenuOption,
      handleDateRangeChange,
      dateRange,
    ],
  );

  const handleCloseModal = useCallback(() => {
    setActiveModalType(null);
    setSelectedUserData(null);
  }, []);

  const modalLicenseComponent = {
    [licenseActionMenuKeys.VIEW_USER]: (
      <LicenseViewUserDetails
        userData={selectedUserData}
        onCloseModal={handleCloseModal}
        onUserLicenseUpdate={onUserLicenseUpdate}
      />
    ),
    [licenseActionMenuKeys.ASSIGN_LICENSE]: (
      <AssignLicense
        userData={selectedUserData}
        onCloseModal={handleCloseModal}
        onUserLicenseUpdate={onUserLicenseUpdate}
      />
    ),
    [licenseActionMenuKeys.RENEW_LICENSE]: (
      <RenewLicense
        userData={selectedUserData}
        onCloseModal={handleCloseModal}
        onUserLicenseUpdate={onUserLicenseUpdate}
      />
    ),
    [licenseActionMenuKeys.TERMINATE_LICENSE]: (
      <TerminateLicense
        userData={selectedUserData}
        onCloseModal={handleCloseModal}
        onUserLicenseUpdate={onUserLicenseUpdate}
      />
    ),
  };

  return (
    <>
      <div className="license-management-table">
        <WPAgGrid
          tableHeight="100%"
          rowData={rowData}
          defaultColDef={defaultColDef}
          columnDefs={LICENSE_MANAGEMENT_TABLE_CONFIG}
          gridOptions={gridOptions}
          localeText={{ noRowsToShow: t("licenseManagement.noDataToShow") }}
          rowSelection="none"
        />
      </div>
      {modalLicenseComponent[activeModalType]}
    </>
  );
};

export default LicenseManagementTable;
